import React, { useState } from "react";
import AppAlertDialog from "./AppAlertDialog";
import AppInputNew from "../AppInput/AppInputNew";

/**
 * Example usage of AppAlertDialog showing trigger-based, controlled,
 * danger-variant, async-confirm (loading), and prompt-style (input) modes.
 */
const AppAlertDialogExample: React.FC = () => {
	const [controlledOpen, setControlledOpen] = useState(false);
	const [promptOpen, setPromptOpen] = useState(false);
	const [promptValue, setPromptValue] = useState("");
	const [lastAction, setLastAction] = useState<string>("—");

	const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

	// A plain reusable trigger button (Tailwind-styled with the brand token).
	const triggerButton = (label: string) => (
		<button className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand/90">
			{label}
		</button>
	);

	return (
		<div className="p-6 max-w-xl mx-auto space-y-6">
			<h2 className="text-xl font-bold mb-2">AppAlertDialog Examples</h2>
			<p className="text-sm text-gray-600">
				Last action: <span className="font-semibold">{lastAction}</span>
			</p>

			{/* Trigger-based (uncontrolled) */}
			<div className="space-y-2">
				<h3 className="font-medium">Trigger-based (default)</h3>
				<AppAlertDialog
					trigger={triggerButton("Open dialog")}
					title="Are you sure?"
					description="This is a basic confirmation dialog opened from its own trigger."
					confirmLabel="Confirm"
					cancelLabel="Cancel"
					skipTranslation
					onConfirm={() => setLastAction("Confirmed (default)")}
					onCancel={() => setLastAction("Cancelled (default)")}
				/>
			</div>

			{/* Danger variant */}
			<div className="space-y-2">
				<h3 className="font-medium">Danger variant</h3>
				<AppAlertDialog
					trigger={
						<button className="inline-flex h-9 items-center justify-center rounded-md bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger/90">
							Delete item
						</button>
					}
					variant="danger"
					title="Delete this item?"
					description="This action cannot be undone."
					confirmLabel="Delete"
					cancelLabel="Keep"
					skipTranslation
					onConfirm={() => setLastAction("Deleted")}
				/>
			</div>

			{/* Async confirm with loading */}
			<div className="space-y-2">
				<h3 className="font-medium">Async confirm (loading)</h3>
				<AppAlertDialog
					trigger={triggerButton("Save with delay")}
					title="Save changes?"
					description="The confirm button stays disabled while the (fake) request runs for 1.5s."
					confirmLabel="Save"
					cancelLabel="Cancel"
					skipTranslation
					onConfirm={async () => {
						await wait(1500);
						setLastAction("Saved (async)");
					}}
				/>
			</div>

			{/* Controlled */}
			<div className="space-y-2">
				<h3 className="font-medium">Controlled (open state owned by parent)</h3>
				<button
					className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
					onClick={() => setControlledOpen(true)}
				>
					Open controlled
				</button>
				<AppAlertDialog
					open={controlledOpen}
					onOpenChange={setControlledOpen}
					title="Controlled dialog"
					description="Its open state lives in the parent component."
					skipTranslation
					confirmLabel="Got it"
					cancelLabel="Close"
					onConfirm={() => setLastAction("Confirmed (controlled)")}
				/>
			</div>

			{/* Prompt-style (input) — a replacement for window.prompt */}
			<div className="space-y-2">
				<h3 className="font-medium">Prompt-style (input)</h3>
				<button
					className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:bg-brand/90"
					onClick={() => {
						setPromptValue("");
						setPromptOpen(true);
					}}
				>
					Rename item…
				</button>
				<AppAlertDialog
					open={promptOpen}
					onOpenChange={setPromptOpen}
					title="Rename item"
					description="Enter a new name for this item."
					skipTranslation
					confirmLabel="Save"
					cancelLabel="Cancel"
					// Gate the confirm button until something is typed — like prompt() returning null on empty.
					confirmDisabled={promptValue.trim() === ""}
					onConfirm={() => setLastAction(`Renamed to "${promptValue.trim()}"`)}
					onCancel={() => setLastAction("Rename cancelled")}
				>
					<AppInputNew
						label="Name"
						placeholder="e.g. Q3 Budget"
						value={promptValue}
						onChangeText={setPromptValue}
						selectOnFocus
					/>
				</AppAlertDialog>
			</div>
		</div>
	);
};

export default AppAlertDialogExample;
